// src/components/AttendanceStartCard.jsx
// 出席受付の開始【2026-09-11 ビーコンとOTPを1つにまとめた】
//
// もとは「ビーコンの受付開始」がダッシュボード、「OTP発行」が別ページと
// 分かれていた。教員が授業の直前にやることは「この授業の出席を開始する」
// の一択なので、授業を選んで方法を選ぶ、という1つの操作にする。
//
// 2つの方法は性質がかなり違うので、選ぶところに違いを書いておく:
//   ビーコン … 教室のビーコンを端末が検知して打刻する。教室にいないと打てない
//   OTP      … 6桁の番号を配って入力させる。番号さえ知れば外からでも打てる
// 「どちらでもいい」ものではなく、OTPはビーコンが使えないときの代替。
//
// 開始したあとの状態（受付中のセッション / 有効なコード）は、どちらも
// サーバーから拾い直す。画面の中にだけ持っていると、ページを移動したり
// スマホで他のアプリを見たりしただけで、終了も無効化もできなくなる。

import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PeriodBar, { usePeriod } from './PeriodBar';
import MinutesField from './MinutesField';
import RecheckPanel from './RecheckPanel';

const authHeader = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('token'),
  'Content-Type': 'application/json',
});

// OTPの有効時間。サーバー側の上下限は 1分〜60分（otpController）
const LS_METHOD = 'attendanceStartMethod';
const LS_CLASS  = 'otpLastClassId';
const LS_OTPDUR = 'otpDurationSec';
const LS_LATE   = 'attendanceLateMinutes';   // 遅刻とみなすまでの分数【2026-09-18】

const load = (k, fb) => { try { const v = localStorage.getItem(k); return v === null ? fb : v; } catch (e) { return fb; } };
const save = (k, v) => { try { localStorage.setItem(k, String(v)); } catch (e) { /* 保存できなくても動く */ } };

// 授業日を 'YYYY-MM-DD'（端末の日付）にする【2026-09-28】
//   サーバーは DATE を JSON にすると '2026-09-27T15:00:00.000Z'（= JST の 9/28 0:00）で返すので、
//   先頭10文字を切ると1日前になっていた。日付だけの文字列はそのまま使う。
const ymdLocal = (v) => {
  if (!v) return '';
  const str = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const d = new Date(str);
  if (isNaN(d.getTime())) return str.slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// 時刻を端末の時刻で 'HH:MM' にする【2026-10-08 レビュー】
//   以前は String(v).slice(11, 16) で、サーバーの返す UTC のまま出ていた（9時間ずれ）。
const hmLocal = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
};
// 'HH:MM[:SS]' → その日の分
const toMin = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const hm = (t) => String(t || '').slice(0, 5);
const todayYmd = () => ymdLocal(new Date().toISOString());

const fmtTime = (sec) => {
  const m = Math.floor(sec / 60), s = sec % 60;
  return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
};

// onClassChange … 選んでいる授業が変わったとき（同じページの「手動で出席を記録」を合わせるため）【2026-10-13】
export default function AttendanceStartCard({ user, classes: rawClasses = [], onClassChange }) {
  // 出席を取るのは今の年度の授業だけ。学期は今の期間（補講などで前の学期を選ぶこともできる）【2026-09-17】
  const period = usePeriod({ lockYear: true });
  // 今日の曜日時限（今の学期の授業だけ）【2026-10-08 レビュー】
  //   ・今日ある授業を一覧の先頭に出し、時間内ならその授業を最初から選んでおく
  //   ・時限の遅刻の分数・終了時刻をそのまま使えるようにする（以前は前回の値と90分が初期値だった）
  const [todaySlots, setTodaySlots] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch('/api/schedule/classes?date=' + todayYmd(), { headers: authHeader() })
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        setTodaySlots(((d && d.classes) || []).filter(r => r.schedule_id != null && r.is_current_term !== false));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // いまがその時限の時間内（受付開始の30分前〜終了）か
  const slotNow = useCallback((cid) => {
    const now = new Date();
    const m = now.getHours() * 60 + now.getMinutes();
    return todaySlots.find(s => {
      if (String(s.id) !== String(cid)) return false;
      const st = toMin(s.attendance_start_time || s.start_time), en = toMin(s.end_time);
      return st != null && en != null && m >= st - 30 && m <= en;
    }) || null;
  }, [todaySlots]);
  const todayIds = useMemo(() => new Set(todaySlots.map(s => String(s.id))), [todaySlots]);

  const allUnique = useMemo(() => {
    const seen = new Map();
    for (const c of rawClasses) if (!seen.has(c.id)) seen.set(c.id, c);   // 曜日時限ぶん重なるので1つに
    return [...seen.values()];
  }, [rawClasses]);
  const classes = useMemo(() => {
    const list = period.filter(allUnique);
    // 今日ある授業を先頭に（並びはそのまま）
    return [...list.filter(c => todayIds.has(String(c.id))), ...list.filter(c => !todayIds.has(String(c.id)))];
  }, [allUnique, period.filter, todayIds]); // eslint-disable-line react-hooks/exhaustive-deps
  const hiddenByPeriod = allUnique.length - period.filter(allUnique).length;
  const [method, setMethod]   = useState(() => (load(LS_METHOD, 'beacon') === 'otp' ? 'otp' : 'beacon'));
  const [classId, setClassId] = useState(() => load(LS_CLASS, ''));
  const [autoCloseMin, setAutoCloseMin] = useState('90');
  const [otpSec, setOtpSec]   = useState(() => Number(load(LS_OTPDUR, 600)) || 600);
  // 遅刻の基準【2026-09-18】'' = 遅刻判定しない、数字 = 受付開始から その分数 後
  const [lateMin, setLateMin] = useState(() => {
    const v = load(LS_LATE, '15');
    return v === 'none' ? '' : (v || '15');
  });
  // 授業スケジュールの設定どおりにする（時限の時間内のときだけ選べる。既定はこちら）【2026-10-08】
  const [useSched, setUseSched] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState(null);          // { text, isError }

  // 動いているものは全部出す【2026-09-11】
  //   1件しか持っていないと、複数の授業で開けているとき残りを閉じられない。
  //   「いま何が動いているか」が画面から分からないのが一番まずい。
  const [openSessions, setOpenSessions] = useState([]);   // ビーコン: 受付中のセッション
  const [otps, setOtps]                 = useState([]);   // OTP: 有効なコード
  const [showForm, setShowForm]         = useState(false); // 何か動いていても開始したいとき
  const [remain, setRemain]     = useState(0);
  const [closed, setClosed]     = useState([]);           // 終了した回
  const [showClosed, setShowClosed] = useState(false);
  // 終了した回の開き直し方を選ぶ行と、選んだ方法（null = 選択中 / 'recheck' = もう一度確認）【2026-09-28】
  const [reopenRow, setReopenRow]   = useState(null);
  const [reopenMode, setReopenMode] = useState(null);
  const [lastRecheck, setLastRecheck] = useState(null);   // 締めた確認の結果（受付中の一覧から消えても残す）
  const restored = useRef(false);

  const beaconMissing = !user || user.beacon_major == null || user.beacon_minor == null;
  const selected = classes.find(c => String(c.id) === String(classId)) || null;
  const slot = selected ? slotNow(selected.id) : null;
  const schedMode = useSched && !!slot;
  // 時限の遅刻の時刻（授業の開始＋遅刻の分数）。分数が無い時限は null
  const slotLateAt = slot && slot.late_threshold_minutes != null
    ? (toMin(slot.start_time) ?? 0) + Number(slot.late_threshold_minutes) : null;

  // 授業の初期値。前回選んだものが今も担当なら、それを使う。
  //   ただし、いま時限の時間内の授業があれば、最初の1回だけそちらを選ぶ【2026-10-08】
  const autoPicked = useRef(false);
  useEffect(() => {
    if (classes.length === 0) return;
    if (!autoPicked.current && todaySlots.length > 0) {
      autoPicked.current = true;
      const now = classes.find(c => slotNow(c.id));
      if (now) { setClassId(String(now.id)); return; }
    }
    if (!classes.some(c => String(c.id) === String(classId))) setClassId(String(classes[0].id));
  }, [classes, classId, todaySlots, slotNow]);
  const onClassChangeRef = useRef(onClassChange);
  onClassChangeRef.current = onClassChange;
  useEffect(() => { if (onClassChangeRef.current && classId) onClassChangeRef.current(String(classId)); }, [classId]);

  // ビーコンが使えないアカウントは、最初からOTP側に寄せる
  useEffect(() => {
    if (beaconMissing && method === 'beacon' && !restored.current) setMethod('otp');
  }, [beaconMissing, method]);

  // ---- いまの状態をサーバーから拾い直す -----------------------------------
  const loadOpen = useCallback(async () => {
    try {
      const r = await fetch('/api/attendance/sessions/history?status=active&limit=20', { headers: authHeader() });
      const d = await r.json();
      setOpenSessions(((d && (d.sessions || d.data || d.rows)) || []).map(x => ({
        sessionId: x.session_id, classId: x.class_id, className: x.class_name,
        sessionDate: x.session_date, autoCloseAt: x.auto_close_at || null,
        present: Number(x.present) || 0, late: Number(x.late) || 0,
      })));
    } catch (e) { /* 拾えなくても開始はできる */ }
  }, []);

  const loadClosed = useCallback(async () => {
    try {
      const r = await fetch('/api/attendance/sessions/history?status=closed&limit=8', { headers: authHeader() });
      const d = await r.json();
      setClosed((d && (d.sessions || d.data || d.rows)) || []);
    } catch (e) { setClosed([]); }
  }, []);

  const loadOtp = useCallback(async () => {
    try {
      const r = await fetch('/api/attendance/otp/active', { headers: authHeader() });
      const d = await r.json();
      const codes = (d && d.success && d.codes) || [];
      setOtps(codes);
      if (codes.length > 0) { restored.current = true; setMethod('otp'); }
    } catch (e) { /* 同上 */ }
  }, []);

  useEffect(() => { loadOpen(); loadClosed(); loadOtp(); }, [loadOpen, loadClosed, loadOtp]);

  // OTPの残り時間
  const mainOtp = otps[0] || null;
  useEffect(() => {
    if (!mainOtp) return;
    const calc = () => Math.max(0, Math.floor((new Date(mainOtp.expiresAt).getTime() - Date.now()) / 1000));
    setRemain(calc());
    const t = setInterval(() => { const r = calc(); setRemain(r); if (r === 0) clearInterval(t); }, 1000);
    return () => clearInterval(t);
  }, [mainOtp]);

  // 遅刻の基準を開始リクエストに載せる【2026-09-18】
  //   受付を開始した時刻が起点なので、分数だけ送ってサーバー側で足す。
  const lateBody = () => {
    if (schedMode) return {};   // 何も送らない＝サーバーが時限の設定で判定する【2026-10-08】
    save(LS_LATE, lateMin === '' ? 'none' : lateMin);
    return lateMin === '' ? { lateRule: 'none' } : { lateMinutes: parseInt(lateMin, 10) };
  };

  /** 「ここから先は遅刻」をその場で決める。押した瞬間が境目になる */
  const markLateNow = async (s2) => {
    if (!s2) return;
    if (!window.confirm(`「${s2.className}」の出席をここで締め切り、これ以降の打刻を遅刻にしますか？`)) return;
    setBusy(true);
    try {
      const r = await fetch('/api/attendance/sessions/' + s2.sessionId + '/late-now', {
        method: 'POST', headers: authHeader(),
      });
      const d = await r.json();
      setMsg({ text: d.message || (d.success ? '遅刻の境目を今にしました' : '設定に失敗しました'),
               isError: !d.success });
    } catch (e) {
      setMsg({ text: '通信エラー: ' + e.message, isError: true });
    } finally { setBusy(false); }
  };

  // ---- 開始 ---------------------------------------------------------------
  const start = async () => {
    setBusy(true); setMsg(null);
    try {
      if (method === 'beacon') {
        if (!selected) { setMsg({ text: '授業を選択してください', isError: true }); return; }
        if (beaconMissing) {
          setMsg({ text: 'このアカウントにビーコンが割り当てられていません。教務にお問い合わせください', isError: true });
          return;
        }
        const body = { major: user.beacon_major, minor: user.beacon_minor, className: selected.name };
        if (schedMode) { /* 送らない＝サーバーが時限の終了時刻で自動終了する【2026-10-08】 */ }
        else if (autoCloseMin) body.autoCloseAt = new Date(Date.now() + parseInt(autoCloseMin, 10) * 60 * 1000).toISOString();
        else body.autoClose = false;   // 「自動で終了しない」を選んだ（送らないとサーバーが時刻を決める）【2026-10-01】
        Object.assign(body, lateBody());
        const r = await fetch('/api/attendance/open-session', {
          method: 'POST', headers: authHeader(), body: JSON.stringify(body),
        });
        const d = await r.json();
        if (!r.ok || !d.success) { setMsg({ text: d.message || 'セッション開始に失敗しました', isError: true }); return; }
        const closeAt = d.session && d.session.autoCloseAt;
        setMsg({ text: schedMode ? `出席受付を開始しました（${closeAt ? hmLocal(closeAt) : hm(slot.end_time)}に自動終了）`
                     : autoCloseMin ? `出席受付を開始しました（${autoCloseMin}分後に自動終了）`
                                    : '出席受付を開始しました', isError: false });
        setShowForm(false);
        await loadOpen();
      } else {
        const body = { expiresInSeconds: otpSec };
        if (classId) body.classId = parseInt(classId, 10);
        Object.assign(body, lateBody());
        save(LS_CLASS, classId); save(LS_OTPDUR, otpSec);
        const r = await fetch('/api/attendance/otp/generate', {
          method: 'POST', headers: authHeader(), body: JSON.stringify(body),
        });
        const d = await r.json();
        if (!d.success) { setMsg({ text: d.message || 'コードの発行に失敗しました', isError: true }); return; }
        setMsg({ text: 'コードを発行しました。学生に伝えてください', isError: false });
        setShowForm(false);
        await loadOtp();
      }
      save(LS_METHOD, method);
    } catch (e) {
      setMsg({ text: '通信エラー: ' + e.message, isError: true });
    } finally { setBusy(false); }
  };

  /** 受付を今すぐ終了する。silent=true はまとめて終了するときの内部呼び出し */
  const closeSession = async (s, silent) => {
    if (!s) return false;
    if (!silent) setBusy(true);
    try {
      const r = await fetch('/api/attendance/close-session', {
        method: 'POST', headers: authHeader(), body: JSON.stringify({ sessionId: s.sessionId }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        if (!silent) setMsg({ text: d.message || 'セッション終了に失敗しました', isError: true });
        return false;
      }
      if (!silent) {
        setMsg({ text: `「${s.className}」の受付を終了しました`, isError: false });
        await loadOpen(); await loadClosed();
      }
      return true;
    } catch (e) {
      if (!silent) setMsg({ text: '通信エラー: ' + e.message, isError: true });
      return false;
    } finally { if (!silent) setBusy(false); }
  };

  const invalidateOtp = async (c, silent) => {
    if (!c) return false;
    if (!silent) setBusy(true);
    try {
      const r = await fetch('/api/attendance/otp/invalidate', {
        method: 'POST', headers: authHeader(), body: JSON.stringify({ code: c.code }),
      });
      const d = await r.json();
      if (!d.success) {
        if (!silent) setMsg({ text: d.message || '無効化に失敗しました', isError: true });
        return false;
      }
      if (!silent) { setMsg({ text: 'コードを無効化しました', isError: false }); await loadOtp(); }
      return true;
    } catch (e) {
      if (!silent) setMsg({ text: '通信エラー: ' + e.message, isError: true });
      return false;
    } finally { if (!silent) setBusy(false); }
  };

  /**
   * いま動いているものを全部その場で止める【2026-09-11】
   * 授業が終わったのに受付が開きっぱなし、という状態を1回で片付けられるようにする。
   */
  const stopAll = async () => {
    const n = openSessions.length + otps.length;
    if (n === 0) return;
    if (!window.confirm(`受付中 ${openSessions.length} 件とコード ${otps.length} 件を、いますぐ止めますか？`)) return;
    setBusy(true); setMsg(null);
    let ok = 0;
    for (const s of openSessions) { if (await closeSession(s, true)) ok++; }
    for (const c of otps)         { if (await invalidateOtp(c, true)) ok++; }
    await loadOpen(); await loadOtp(); await loadClosed();
    setBusy(false);
    setMsg({ text: `${ok} 件を止めました` + (ok < n ? `（${n - ok} 件は失敗）` : ''), isError: ok === 0 });
  };

  /** 終了した回を開き直す（ビーコンのみ） */
  const reopen = async (s) => {
    if (beaconMissing) { setMsg({ text: 'ビーコンが割り当てられていないため開き直せません', isError: true }); return; }
    if (!window.confirm(`${ymdLocal(s.session_date)} の「${s.class_name}」の受付を開き直しますか？`)) return;
    setBusy(true); setMsg(null);
    try {
      const body = { major: user.beacon_major, minor: user.beacon_minor,
                     className: s.class_name, sessionId: s.session_id };
      const sched = useSched && !!slotNow(s.class_id);
      if (sched) { /* 送らない＝サーバーが時限の終了時刻で決める */ }
      else if (autoCloseMin) body.autoCloseAt = new Date(Date.now() + parseInt(autoCloseMin, 10) * 60 * 1000).toISOString();
      else body.autoClose = false;   // 「自動で終了しない」を選んだ（送らないとサーバーが時刻を決める）【2026-10-01】
      const r = await fetch('/api/attendance/open-session', {
        method: 'POST', headers: authHeader(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setMsg({ text: d.message || '再開に失敗しました', isError: true }); return; }
      setShowClosed(false);
      await loadOpen();
      setMsg({ text: `${ymdLocal(s.session_date)} の回を開き直しました`
                   + (sched ? '' : autoCloseMin ? `（${autoCloseMin}分後に自動終了）` : '（手動で終了してください）'),
               isError: false });
      loadClosed();
    } catch (e) { setMsg({ text: '通信エラー: ' + e.message, isError: true }); }
    finally { setBusy(false); }
  };

  // =========================================================================
  //  表示
  // =========================================================================
  const live = openSessions.length + otps.length;

  // OTP の有効時間と遅刻の境目の比べ（遅刻になるまでの分数。警告が要らなければ 0）
  const otpMinutes = Math.max(1, Math.round(otpSec / 60));
  const minutesToLate = (() => {
    if (schedMode) {
      if (slotLateAt == null) return 0;
      const now = new Date();
      return Math.max(0, slotLateAt - (now.getHours() * 60 + now.getMinutes()));
    }
    return lateMin === '' ? 0 : (parseInt(lateMin, 10) || 0);
  })();
  const otpLateWarn = method === 'otp' && minutesToLate > 0 && otpMinutes <= minutesToLate ? minutesToLate : 0;


  const METHODS = [
    {
      key: 'beacon', title: 'ビーコン（BLE）', badge: '教室にいる人だけ',
      desc: '教室に置いたビーコンの電波を学生のアプリが受け取って打刻します。'
          + '電波が届く範囲にいないと打てないので、代返がしにくいのが利点です。ふだんはこちら。',
      note: '教員アカウントにビーコンが割り当てられている必要があります。',
      disabled: beaconMissing,
      disabledNote: 'このアカウントにはビーコンが割り当てられていません。教務にお問い合わせください。',
    },
    {
      key: 'otp', title: 'OTPコード（6桁）', badge: '番号を知っていれば打てる',
      desc: '6桁の番号を黒板やスクリーンに出して、学生がアプリに入力します。'
          + 'ビーコンが無い教室や、ビーコンが不調なときの代わりです。',
      note: '番号さえ分かれば教室の外からでも打てます。その場で伝えて、短い有効時間で締めてください。',
      disabled: false,
    },
  ];

  return (
    <div className="card" style={{ borderLeft: '4px solid #22c55e' }}>
      <div className="card-header"><h2>出席を受け付ける</h2></div>

      {/* ---- いま動いているもの【2026-09-11】 ----
           受付中のセッションと有効なコードを全部出して、1件ずつその場で止められる
           ようにする。1件しか持っていないと、複数開けたときに残りを閉じられない。 */}
      {live > 0 && (
        <div style={{ marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem',
                        flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            <strong style={{ fontSize: '0.95rem', color: '#166534' }}>
              いま受付中：{live} 件
            </strong>
            {live > 1 && (
              <button className="btn-danger" onClick={stopAll} disabled={busy}
                      style={{ padding: '0.25rem 0.8rem', fontSize: '0.85rem' }}>
                すべて即終了
              </button>
            )}
          </div>

          {openSessions.map(s2 => (
            <div key={s2.sessionId}
                 style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap',
                          border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10,
                          padding: '0.6rem 0.8rem', marginBottom: '0.45rem' }}>
              <span style={{ flex: 1, minWidth: 180 }}>
                <strong>{s2.className}</strong>
                <span style={{ color: '#4b5563', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                  ビーコン
                  {s2.autoCloseAt && '　自動終了 ' + hmLocal(s2.autoCloseAt)}
                </span>
                <div style={{ fontSize: '0.82rem', color: '#4b5563' }}>
                  打刻 {s2.present + s2.late} 人
                  {s2.sessionDate && '　' + ymdLocal(s2.sessionDate)}
                </div>
              </span>
              <button className="btn-secondary" disabled={busy}
                      onClick={() => markLateNow(s2)}
                      title="押した時点を境にして、これ以降の打刻を遅刻として記録します"
                      style={{ minHeight: '2.4rem' }}>
                今から遅刻
              </button>
              <button className="btn-danger" disabled={busy}
                      onClick={() => closeSession(s2)}
                      style={{ minHeight: '2.4rem' }}>
                いますぐ終了
              </button>
              {/* 途中や最後にもう一度打刻させる【2026-09-28】 */}
              <RecheckPanel session={s2} beaconMissing={beaconMissing} otpSec={otpSec} sessionActive
                            onChanged={() => { loadOpen(); loadOtp(); loadClosed(); }}
                            onClosed={(r) => setLastRecheck({ ...r, className: s2.className })} />
            </div>
          ))}

          {otps.map((c, i) => (
            <div key={c.code}
                 style={{ border: '2px solid #2563eb',
                          background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                          borderRadius: 12, padding: '0.8rem', marginBottom: '0.45rem',
                          textAlign: 'center' }}>
              <div style={{ fontSize: '1rem', color: '#1e3a8a', fontWeight: 700 }}>{c.className}</div>
              <div style={{ fontSize: '2.6rem', fontWeight: 800, color: '#1e3a8a',
                            letterSpacing: '0.3rem', fontFamily: "'Courier New', monospace",
                            padding: '0.3rem 0' }}>
                {String(c.code).split('').join(' ')}
              </div>
              <div style={{ fontSize: '0.95rem' }}>
                {i === 0
                  ? <>残り <strong style={{ color: remain < 60 ? '#ef4444' : '#1e3a8a' }}>{fmtTime(remain)}</strong></>
                  : <>失効 {hmLocal(c.expiresAt)}</>}
                {c.usedCount != null && (
                  <span style={{ marginLeft: '0.8rem', fontSize: '0.88rem', color: '#4b5563' }}>
                    打刻 {c.usedCount} 人
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center',
                            marginTop: '0.7rem', flexWrap: 'wrap' }}>
                <button className="btn-secondary" onClick={() => navigator.clipboard?.writeText(c.code)}>
                  コピー
                </button>
                <button className="btn-danger" disabled={busy}
                        onClick={() => { if (window.confirm('このコードを無効化しますか？')) invalidateOtp(c); }}>
                  いますぐ無効化
                </button>
              </div>
            </div>
          ))}

          {!showForm && (
            <button className="btn-secondary" onClick={() => setShowForm(true)}
                    style={{ fontSize: '0.85rem' }}>
              別の授業でも開始する…
            </button>
          )}
        </div>
      )}

      {/* ---- 開始フォーム ---- */}
      {(live === 0 || showForm) && (
        <>
          <div className="form-row">
            <label>授業</label>
            <PeriodBar period={period} />
            <select value={classId} onChange={e => { setClassId(e.target.value); save(LS_CLASS, e.target.value); }}>
              {classes.length === 0 && <option value="">この期間の担当授業がありません</option>}
              {method === 'otp' && <option value="">— 自動判定（現在の時間帯） —</option>}
              {classes.map(c => {
                const t = todaySlots.find(s => String(s.id) === String(c.id));
                return (
                  <option key={c.id} value={c.id}>
                    {t ? `【今日 ${hm(t.start_time)}〜】` : ''}{c.name}{c.semester ? `（${c.semester}）` : ''}
                  </option>
                );
              })}
            </select>
            {/* 学期の登録がずれた授業も、ここから選べるようにする【2026-10-08 レビュー】 */}
            {classes.length === 0 && hiddenByPeriod > 0 && period.termId !== 'all' && period.year != null && (
              <small style={{ color: '#b45309' }}>
                ほかの学期に担当授業が {hiddenByPeriod} 件あります。{' '}
                <button type="button" className="btn-secondary" style={{ padding: '0.1rem 0.5rem', fontSize: '0.8rem' }}
                        onClick={() => period.select(period.year, 'all')}>
                  すべての学期から選ぶ
                </button>
              </small>
            )}
          </div>

          {/* 授業スケジュールの設定どおり【2026-10-08 レビュー】
              いま時限の時間内の授業なら、時限の遅刻の分数と終了時刻をそのまま使う。 */}
          {slot && (
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.6rem',
                            padding: '0.55rem 0.75rem', borderRadius: 10,
                            border: '1px solid ' + (useSched ? '#86efac' : '#e5e7eb'),
                            background: useSched ? '#f0fdf4' : '#fff', cursor: 'pointer' }}>
              <input type="checkbox" checked={useSched} onChange={e => setUseSched(e.target.checked)}
                     style={{ marginTop: '0.25rem' }} />
              <span style={{ fontSize: '0.88rem', lineHeight: 1.6 }}>
                <strong>授業スケジュールの設定どおり</strong>
                <span style={{ color: '#4b5563' }}>
                  （{hm(slot.start_time)}〜{hm(slot.end_time)}
                  {slot.late_threshold_minutes != null ? `・開始${slot.late_threshold_minutes}分後から遅刻` : ''}
                  {method === 'beacon' ? `・${hm(slot.end_time)}に自動終了` : ''}）
                </span>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  外すと、この回だけ遅刻{method === 'beacon' ? 'と自動終了' : ''}を下で決められます。
                </div>
              </span>
            </label>
          )}

          <div style={{ fontSize: '0.9rem', fontWeight: 700, margin: '1rem 0 0.5rem' }}>
            出席の取り方
          </div>
          {METHODS.map(m => (
            <label key={m.key}
                   style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                            padding: '0.7rem 0.8rem', marginBottom: '0.5rem', borderRadius: 10,
                            border: '1px solid ' + (method === m.key ? '#2563eb' : '#e5e7eb'),
                            background: method === m.key ? '#eff6ff' : '#fff',
                            opacity: m.disabled ? 0.7 : 1,
                            cursor: m.disabled ? 'not-allowed' : 'pointer' }}>
              <input type="radio" name="attendanceMethod" checked={method === m.key}
                     disabled={m.disabled}
                     onChange={() => { setMethod(m.key); save(LS_METHOD, m.key); }}
                     style={{ marginTop: '0.3rem' }} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '0.95rem' }}>{m.title}</strong>
                  <span style={{ background: '#eef2ff', color: '#3730a3', borderRadius: 999,
                                 padding: '0.05rem 0.5rem', fontSize: '0.75rem' }}>{m.badge}</span>
                </span>
                <div style={{ fontSize: '0.85rem', color: '#4b5563', lineHeight: 1.7, marginTop: '0.2rem' }}>
                  {m.desc}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.6, marginTop: '0.2rem' }}>
                  {m.disabled ? m.disabledNote : m.note}
                </div>
              </span>
            </label>
          ))}

          {/* 遅刻の基準【2026-09-18】
              時限（曜日・開始時刻）を登録していない授業や補講では、
              これまで遅刻がまったく付かなかった。受付を開始した時刻を
              起点にすれば、時限の登録に関係なく判定できる。 */}
          {!schedMode && (
          <div className="form-row" style={{ marginTop: '0.8rem' }}>
            <label>遅刻とみなす</label>
            <MinutesField value={lateMin} onChange={setLateMin}
                          presets={[5, 10, 15, 20, 30, 45]}
                          noneLabel="判定しない" max={600}
                          ariaLabel="受付開始から何分後を遅刻にするか" />
            <small style={{ color: '#6b7280' }}>
              受付を開始した時刻が起点です。数字は直接打てます。空にすると遅刻判定をしません。
              始めたあとでも、受付中の欄の「今から遅刻」でその場を境目にできます。
            </small>
          </div>
          )}

          {method === 'beacon' ? (schedMode ? null : (
            <div className="form-row" style={{ marginTop: '0.8rem' }}>
              <label>自動終了</label>
              <MinutesField value={autoCloseMin} onChange={setAutoCloseMin}
                            presets={[30, 60, 90, 100, 120]}
                            noneLabel="なし" max={1440}
                            ariaLabel="何分後に自動で受付を終わるか" />
              <small style={{ color: '#6b7280' }}>
                数字は直接打てます（100分授業なら 100）。空にすると自動終了しません。
                自動終了を待たずに、受付中の欄の「いますぐ終了」でいつでも締められます。
              </small>
            </div>
          )) : (
            <div className="form-row" style={{ marginTop: '0.8rem' }}>
              <label>コードの有効時間</label>
              {/* 分で打てるようにする。内部は秒のまま【2026-09-18】 */}
              <MinutesField value={String(Math.max(1, Math.round(otpSec / 60)))}
                            onChange={v => setOtpSec(Math.max(1, parseInt(v || '1', 10)) * 60)}
                            presets={[3, 5, 10, 15, 30]} unit="分" max={180}
                            ariaLabel="コードが使える時間（分）" />
              <small style={{ color: '#6b7280' }}>
                数字は直接打てます。長くするほど、教室の外にコードを回される余地が増えます。
                途中で「いますぐ無効化」もできます。
              </small>
              {/* 有効時間が遅刻の判定より短いと、遅刻になる前にコードが切れる【2026-10-08 レビュー】 */}
              {otpLateWarn && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.85rem', color: '#b45309', background: '#fffbeb',
                              border: '1px solid #fde68a', borderRadius: 8, padding: '0.4rem 0.6rem', lineHeight: 1.6 }}>
                  コードの有効時間（{otpMinutes}分）が、遅刻になるまで（あと{otpLateWarn}分）より短いです。
                  遅れて来た学生はコードが切れていて打刻できず、遅刻ではなく欠席になります。
                  遅刻も記録するなら、有効時間を {otpLateWarn} 分より長くしてください。
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.2rem', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={start}
                    disabled={busy || (method === 'beacon' && (beaconMissing || classes.length === 0))}
                    style={{ flex: 1, minWidth: '14rem', minHeight: '3rem', fontSize: '1.05rem' }}>
              {busy ? '処理中…'
                    : method === 'beacon' ? '出席受付を開始（ビーコン）'
                    : 'コードを発行（' + Math.max(1, Math.round(otpSec / 60)) + '分）'}
            </button>
            {live > 0 && (
              <button className="btn-secondary" onClick={() => setShowForm(false)} disabled={busy}>
                やめる
              </button>
            )}
          </div>
        </>
      )}

      {msg && (
        <p style={{ marginTop: '0.6rem', color: msg.isError ? '#c62828' : '#166534', fontWeight: 600 }}>
          {msg.text}
        </p>
      )}

      {/* ---- 前回の「もう一度確認」の結果【2026-09-28】----
           確認を締めると回は終了に戻り、受付中の一覧から消える。結果が一緒に消えないよう、ここに残す。 */}
      {lastRecheck && (
        <div style={{ marginTop: '0.8rem', border: '1px solid #fecaca', background: '#fef2f2',
                      borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.88rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong style={{ flex: 1, color: lastRecheck.missing.length ? '#b91c1c' : '#166534' }}>
              「{lastRecheck.className}」{lastRecheck.round}回目の確認：応えた {lastRecheck.marked} / {lastRecheck.attended} 人
            </strong>
            <button type="button" className="btn-secondary" onClick={() => setLastRecheck(null)}
                    style={{ padding: '0.1rem 0.5rem', fontSize: '0.8rem' }}>閉じる</button>
          </div>
          {lastRecheck.missing.length > 0 ? (
            <>
              <div style={{ margin: '0.3rem 0 0.2rem', color: '#7f1d1d' }}>応えていない学生（{lastRecheck.missing.length}人）</div>
              {lastRecheck.missing.map(m => (
                <div key={m.userId}>{m.studentId || ''}　{m.name || ''}</div>
              ))}
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.25rem' }}>
                出欠は変えていません（お知らせにも残してあります）。欠席などに変えるときは、出席記録から手で直してください。
              </div>
            </>
          ) : <div style={{ color: '#166534' }}>全員が応えました。</div>}
        </div>
      )}

      {/* ---- 終了した回を開き直す【2026-09-28 選べるようにした】----
           ・普通に再開 … 今までどおり。新しい出席も取れる（iOS の教員アプリの「再開」もこちら）
           ・もう一度確認として再開 … 最初に出席した学生の2回目だけを受け付ける（途中で抜けた人の確認） */}
      {closed.length > 0 && (
        <div style={{ marginTop: '0.9rem', paddingTop: '0.7rem', borderTop: '1px dashed #d1d5db' }}>
          <button type="button" className="btn-secondary" onClick={() => setShowClosed(v => !v)}
                  style={{ fontSize: '0.85rem' }}>
            {showClosed ? '閉じる' : '終了した回を開き直す…'}
          </button>
          {showClosed && (
            <>
              <p style={{ fontSize: '0.82rem', color: '#4b5563', margin: '0.6rem 0 0.4rem', lineHeight: 1.7 }}>
                開き直し方を選べます。<strong>普通に再開</strong>は今までどおり（まだの学生も出席できます）。
                <strong>もう一度確認</strong>は、最初に出席した学生だけにもう一度打刻してもらい、途中で抜けた人を見つけます。
              </p>
              {closed.map(s => {
                const open = reopenRow === s.session_id;
                return (
                  <div key={s.session_id}
                       style={{ borderBottom: '1px solid #f3f4f6', padding: '0.45rem 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                      <span style={{ whiteSpace: 'nowrap', color: '#4b5563', fontSize: '0.85rem' }}>
                        {ymdLocal(s.session_date)}
                      </span>
                      <span style={{ flex: 1, minWidth: 140, fontWeight: 600 }}>{s.class_name}</span>
                      <span style={{ color: '#4b5563', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        出席 {(Number(s.present) || 0) + (Number(s.late) || 0)}
                      </span>
                      <button type="button" className={open ? 'btn-primary' : 'btn-secondary'} disabled={busy}
                              style={{ padding: '0.25rem 0.7rem', fontSize: '0.82rem' }}
                              onClick={() => { setReopenRow(open ? null : s.session_id); setReopenMode(null); }}>
                        {open ? 'やめる' : '再開…'}
                      </button>
                    </div>
                    {open && reopenMode !== 'recheck' && (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.45rem' }}>
                        <button type="button" className="btn-secondary" disabled={busy || beaconMissing}
                                title={beaconMissing ? 'ビーコンが割り当てられていないため使えません' : 'まだの学生も出席できる、ふつうの受付に戻します'}
                                onClick={() => { setReopenRow(null); reopen(s); }}>
                          普通に再開
                        </button>
                        <button type="button" className="btn-primary" disabled={busy}
                                title="最初に出席した学生だけに、もう一度打刻してもらいます"
                                onClick={() => setReopenMode('recheck')}>
                          もう一度確認として再開
                        </button>
                      </div>
                    )}
                    {open && reopenMode === 'recheck' && (
                      <RecheckPanel session={{ sessionId: s.session_id, classId: s.class_id, className: s.class_name }}
                                    startOpen beaconMissing={beaconMissing} otpSec={otpSec}
                                    onChanged={() => { setReopenRow(null); setReopenMode(null); setShowClosed(false);
                                                       loadOpen(); loadOtp(); loadClosed(); }}
                                    onDone={() => setReopenMode(null)} />
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
